import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  getSubscriptionStatus,
  createCheckoutSession,
  createPortalSession,
  SubscriptionStatus,
  SubscriptionFeatures,
  PriceType,
} from '../../api/subscription_api';

interface SubscriptionContextType {
  subscription: SubscriptionStatus | null;
  loading: boolean;
  error: string | null;
  isPro: boolean;
  features: SubscriptionFeatures;
  ownsPack: (packType: string) => boolean;
  checkout: (priceType: PriceType) => Promise<void>;
  openPortal: () => Promise<void>;
  refresh: () => Promise<void>;
}

const FREE_FEATURES: SubscriptionFeatures = {
  maxQuestionsPerDomain: 10,
  timedQuizAccess: false,
  analyticsAccess: false,
  allDomainsAccess: false,
  examSimulation: false,
  studyPlanAccess: false,
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const status = await getSubscriptionStatus();
      setSubscription(status);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      setError('Could not load subscription info');
      // Default to free tier on error so app still works
      setSubscription({
        tier: 'free',
        status: 'active',
        features: FREE_FEATURES,
        ownedPacks: [],
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const isPro =
    subscription?.tier === 'pro_monthly' || subscription?.tier === 'pro_yearly';

  const features = subscription?.features || FREE_FEATURES;

  const ownsPack = (packType: string): boolean => {
    return subscription?.ownedPacks?.includes(packType) ?? false;
  };

  const checkout = async (priceType: PriceType) => {
    try {
      const url = await createCheckoutSession(priceType);
      window.location.href = url;
    } catch (err) {
      console.error('Checkout failed:', err);
      setError('Failed to start checkout. Please try again.');
    }
  };

  const openPortal = async () => {
    try {
      const url = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      console.error('Portal session failed:', err);
      setError('Failed to open billing portal.');
    }
  };

  const value: SubscriptionContextType = {
    subscription,
    loading,
    error,
    isPro,
    features,
    ownsPack,
    checkout,
    openPortal,
    refresh: fetchStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
